import React from "react"
import { Link } from "gatsby"

import {
  FEATURE_OFFER_COPY,
  getFeatureDescription,
} from "../constants/features"
import { computeTopicStats } from "./topicStats"
import { sortCommunities } from "./communities"
import { readableNumber } from "./text"

const TOP_COMMUNITIES = 4
const TOP_TOPICS = 5
const communityLinkClass = "font-semibold underline"

function directoryPhrase(siteName) {
  return `the ${siteName} directory of communities`
}

function joinNames(items) {
  if (!items.length) return ""
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

function sampleSizeNote(total) {
  if (!total || total >= 30) return ""
  if (total < 15) {
    return " We only have a handful of listings so far, so treat these numbers as directional."
  }
  return " This is a smaller slice of our database, so read trends as directional."
}

function toMembers(c) {
  const n = Number(c.members)
  return Number.isFinite(n) && n > 0 ? n : null
}

function isFreeAccess(c) {
  return !c.paid && !c.apply
}

function joinPlainCommunityNames(communities) {
  return joinNames(communities.map(c => c.name))
}

function joinLinkedCommunities(communities) {
  if (!communities.length) return null
  return communities.map((c, index) => (
    <React.Fragment key={c.slug}>
      {index > 0 && (index === communities.length - 1 ? ", and " : ", ")}
      <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
        {c.name}
      </Link>
    </React.Fragment>
  ))
}

function featureSubjectLabel(feature) {
  const slug = feature.slug
  if (slug === "forum") return "forum communities"
  if (slug === "chat") return "chat communities"
  if (slug === "paid") return "paid membership communities"
  const offer = FEATURE_OFFER_COPY[slug]
  if (offer) return `communities with ${offer}`
  return `communities with ${feature.name.toLowerCase()}`
}

function introQuestion(feature) {
  const slug = feature.slug
  if (slug === "forum") return "What are forum communities on Hive Index?"
  if (slug === "chat") return "What are chat communities on Hive Index?"
  if (slug === "paid")
    return "What are paid membership communities on Hive Index?"
  const offer = FEATURE_OFFER_COPY[slug]
  if (offer) return `What are communities with ${offer} on Hive Index?`
  return `What are communities with ${feature.name.toLowerCase()} on Hive Index?`
}

function collectTopicCounts(communities, topicNodes) {
  const counts = {}
  communities.forEach(c => {
    if (!c.topics) return
    c.topics
      .split(",")
      .map(t => t.trim())
      .filter(Boolean)
      .forEach(slug => {
        counts[slug] = (counts[slug] || 0) + 1
      })
  })
  return Object.entries(counts)
    .map(([slug, count]) => ({
      topic: topicNodes.find(t => t.slug === slug),
      count,
    }))
    .filter(entry => entry.topic)
    .sort((a, b) => b.count - a.count)
}

function buildFeaturePageFAQ({
  feature,
  communities,
  platforms,
  features,
  topicNodes = [],
  updatedDate,
  siteName = "Hive Index",
  templateFeatureSlugs = [],
}) {
  const stats = computeTopicStats({
    topic: null,
    communities,
    platforms,
    features,
    reviews: [],
  })

  const total = stats.total
  if (!total) return []

  const subject = featureSubjectLabel(feature)
  const directory = directoryPhrase(siteName)
  const featureLead =
    feature.description?.trim() ||
    getFeatureDescription(feature.slug, feature.name)
  const featurePageUrl = `/features/${feature.slug}/`

  const topRated = stats.highestRatedCommunities.slice(0, TOP_COMMUNITIES)
  const topPopular = sortCommunities("Popular", [...communities]).slice(
    0,
    TOP_COMMUNITIES
  )
  const topFree = sortCommunities(
    "Popular",
    communities.filter(isFreeAccess)
  ).slice(0, TOP_COMMUNITIES)

  const faqs = []

  faqs.push({
    question: introQuestion(feature),
    answerPlain: `${featureLead} ${siteName} lists ${total} ${subject} in ${directory}. Browse by topic or platform to compare size, access, and reviews before joining.`,
    answer: (
      <>
        {featureLead} {siteName} lists {total} {subject} in {directory}. Browse
        by{" "}
        <Link to="/topics/" className={communityLinkClass}>
          topic
        </Link>{" "}
        or{" "}
        <Link to="/platforms/" className={communityLinkClass}>
          platform
        </Link>{" "}
        to compare size, access, and reviews before joining.
      </>
    ),
  })

  faqs.push({
    question: `How many communities with ${feature.name.toLowerCase()} are listed on ${siteName}?`,
    answerPlain: `${siteName} lists ${total} ${subject} in ${directory}.${sampleSizeNote(
      total
    )} Use topic filters on this page to see which categories have the most listings.`,
    answer: (
      <>
        {siteName} lists {total} {subject} in {directory}.
        {sampleSizeNote(total)} Use topic filters on this page to see which
        categories have the most listings.
      </>
    ),
  })

  if (topRated.length >= 2) {
    faqs.push({
      question: `What are the best ${subject} to join?`,
      answerPlain: `${joinPlainCommunityNames(
        topRated
      )} have the highest review scores among ${subject} in ${directory}. Reviews come from ${siteName} members, not an editorial ranking.`,
      answer: (
        <>
          {joinLinkedCommunities(topRated)} have the highest review scores among{" "}
          {subject} in {directory}. Reviews come from {siteName} members, not an
          editorial ranking.
        </>
      ),
    })
  } else if (topPopular.length) {
    faqs.push({
      question: `What are the most popular ${subject} on ${siteName}?`,
      answerPlain: `${joinPlainCommunityNames(
        topPopular
      )} are among the most popular ${subject} in ${directory}, based on visitor interest.`,
      answer: (
        <>
          {joinLinkedCommunities(topPopular)} are among the most popular{" "}
          {subject} in {directory}, based on visitor interest.
        </>
      ),
    })
  }

  const topPlatforms = stats.platformBreakdown.slice(0, 3)
  if (topPlatforms.length) {
    faqs.push({
      question: `Which platforms have the most ${subject}?`,
      answerPlain: `${joinNames(
        topPlatforms.map(p => `${p.name} (${p.count})`)
      )} ${
        topPlatforms.length === 1 ? "hosts" : "host"
      } the most ${subject} in ${directory}.`,
      answer: (
        <>
          {topPlatforms.map((p, index) => (
            <React.Fragment key={p.slug}>
              {index > 0 &&
                (index === topPlatforms.length - 1 ? ", and " : ", ")}
              <Link to={`/platforms/${p.slug}/`} className={communityLinkClass}>
                {p.name}
              </Link>
              {` (${p.count})`}
            </React.Fragment>
          ))}{" "}
          {topPlatforms.length === 1 ? "hosts" : "host"} the most {subject} in{" "}
          {directory}.
        </>
      ),
    })
  }

  const topTopics = collectTopicCounts(communities, topicNodes).slice(
    0,
    TOP_TOPICS
  )
  if (topTopics.length) {
    faqs.push({
      question: `Which topics have the most ${subject}?`,
      answerPlain: `The most common topic categories for ${subject} include ${joinNames(
        topTopics.map(({ topic, count }) => `${topic.name} (${count})`)
      )}.`,
      answer: (
        <ul className="list-disc space-y-1 pl-5">
          {topTopics.map(({ topic, count }) => (
            <li key={topic.slug}>
              <Link
                to={`/topics/${topic.slug}/feature/${feature.slug}/`}
                className={communityLinkClass}
              >
                {topic.name}
              </Link>
              {` (${count})`}
            </li>
          ))}
        </ul>
      ),
    })
  }

  if (feature.slug === "paid") {
    faqs.push({
      question: "Are these communities paid to join?",
      answerPlain: `Every listing on this page is tagged with paid membership on ${siteName}. Pricing and access rules are set by each community.`,
      answer: (
        <p>
          Every listing on this page is tagged with paid membership on{" "}
          {siteName}. Pricing and access rules are set by each community. Open a
          listing to compare options before joining.
        </p>
      ),
    })
  } else {
    const free = stats.accessBreakdown.find(item => item.key === "free")
    const restricted = stats.accessBreakdown.find(
      item => item.key === "restricted"
    )
    if (free && free.count > 0) {
      const freePlain = topFree.length
        ? `${free.count} of ${total} (${
            free.percent
          }%) ${subject} in ${directory} are free to join, including ${joinPlainCommunityNames(
            topFree
          )}.`
        : `${free.count} of ${total} (${free.percent}%) ${subject} in ${directory} are free to join.`
      faqs.push({
        question: `Are there free ${subject}?`,
        answerPlain: restricted?.count
          ? `${freePlain} The remaining ${restricted.count} require payment, an application, or both.`
          : freePlain,
        answer: (
          <>
            <p>
              {free.count} of {total} ({free.percent}%) {subject} in {directory}{" "}
              are free to join
              {topFree.length ? <> , including:</> : "."}
            </p>
            {topFree.length ? (
              <ul className="list-disc space-y-1 pl-5 mt-2">
                {topFree.map(c => (
                  <li key={c.slug}>
                    <Link
                      to={`/communities/${c.slug}/`}
                      className={communityLinkClass}
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            {restricted?.count ? (
              <p className="mt-2">
                The remaining {restricted.count} require payment, an
                application, or both.
              </p>
            ) : null}
          </>
        ),
      })
    }
  }

  const otherFeatures = stats.commonFeatures
    .filter(f => f.slug !== feature.slug)
    .slice(0, 3)
  if (otherFeatures.length) {
    faqs.push({
      question: `What other features do these communities offer?`,
      answerPlain: `Among ${subject}, other common features include ${joinNames(
        otherFeatures.map(f => `${f.name} (${f.percent}%)`)
      )}.`,
      answer: (
        <p>
          Among {subject}, other common features include{" "}
          {otherFeatures.map((f, index) => (
            <React.Fragment key={f.slug}>
              {index > 0 &&
                (index === otherFeatures.length - 1 ? ", and " : ", ")}
              {templateFeatureSlugs.includes(f.slug) ? (
                <Link
                  to={`/features/${f.slug}/`}
                  className={communityLinkClass}
                >
                  {f.name}
                </Link>
              ) : (
                f.name
              )}
              {` (${f.percent}%)`}
            </React.Fragment>
          ))}
          .
        </p>
      ),
    })
  }

  const growthAnswer = `List your community on ${siteName} for free and tag it with ${feature.name.toLowerCase()} so people searching for ${subject} can find you. Paid promotion can feature your listing more prominently across relevant pages.`
  faqs.push({
    question: `How do I list my community with ${feature.name.toLowerCase()} on ${siteName}?`,
    answerPlain: growthAnswer,
    answer: (
      <>
        <Link to="/submit/" className={communityLinkClass}>
          List your community for free
        </Link>{" "}
        on {siteName} and tag it with {feature.name.toLowerCase()} so people
        searching for {subject} can find you.{" "}
        <Link to="/partners/community/" className={communityLinkClass}>
          Paid promotion
        </Link>{" "}
        can feature your listing more prominently across relevant pages. See all{" "}
        <Link to={featurePageUrl} className={communityLinkClass}>
          {feature.name} communities
        </Link>{" "}
        or browse{" "}
        <Link to="/features/" className={communityLinkClass}>
          all feature tags
        </Link>
        .
      </>
    ),
  })

  const freshness = updatedDate
    ? ` These listings in ${directory} were last updated ${updatedDate}.`
    : ""
  faqs.push({
    question: `How does ${siteName} tag communities with ${feature.name.toLowerCase()}?`,
    answer: `Listings are submitted by community builders and researchers, reviewed for relevance, and tagged with features like ${feature.name.toLowerCase()} based on what each community offers. ${siteName} updates platform, member, review, and traffic data where available.${freshness}`,
  })

  return faqs.slice(0, 9)
}

export { buildFeaturePageFAQ }
